import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { switchChain } from '@wagmi/core'
import { Rpc } from 'accounts'
import { Address, Hex, PublicKey } from 'ox'
import { KeyAuthorization } from 'ox/tempo'
import * as React from 'react'
import { Button, Frame, Input } from 'regen-ui'
import { useConnect } from 'wagmi'
import * as z from 'zod/mini'
import Fingerprint from '~icons/lucide/fingerprint'

import * as Auth from '#/lib/auth.js'
import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { wagmiConfig } from '#/lib/config.js'
import { useWebAuthnConnector } from '#/lib/connectors.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import {
  AccessKeyPermissionsEditor,
  AccessKeyScopes,
  type AuthorizeAccessKey,
} from '#/routes/remote/-components/AccessKeyScopes.js'
import { AuthorizeSpend } from '#/routes/remote/-frames/authorize/AuthorizeSpend.js'

const personalSignSearch = z.optional(z.union([z.string(), Rpc.wallet_connect.personalSign]))

const searchSchema = z.object({
  callback: z.optional(z.string()),
  chainId: z.optional(z.string()),
  expiry: z.optional(z.string()),
  keyType: z.optional(z.string()),
  limits: z.optional(
    z.array(z.object({ limit: z.string(), period: z.optional(z.number()), token: z.string() })),
  ),
  personalSign: personalSignSearch,
  pubKey: z.optional(z.string()),
  showDeposit: z.optional(z.union([z.string(), Rpc.wallet_connect.showDeposit])),
  state: z.optional(z.string()),
})

/** Standalone mobile access-key authorization page. */
export const Route = createFileRoute('/remote/auth/mobile')({
  validateSearch: searchSchema,
  component: RouteComponent,
})

function RouteComponent() {
  const { capture } = useAnalytics()
  // Standalone auth still requires a webAuthn session; read-only mode must
  // render the sign-in flow instead of the existing-user approval screen.
  const sessionAddress = Auth.useSessionAddress()
  const search = Route.useSearch()
  const { callback, chainId, expiry, keyType, limits, pubKey, state } = search
  const personalSign = parsePersonalSign(search.personalSign)

  React.useEffect(() => {
    capture('mobile_auth_flow_started', { authed: !!sessionAddress })
  }, [capture, sessionAddress])

  if (!pubKey || !callback || !state)
    return (
      <Frame subtitle="Missing required parameters." title="Authorize Mobile">
        <p className="text-copy-14 text-negative">
          This page requires pubKey, callback, and state parameters.
        </p>
      </Frame>
    )

  const authorizeAccessKey = buildAuthorizeAccessKey({ chainId, expiry, keyType, limits, pubKey })

  return (
    <MobileAuthFlow
      authorizeAccessKey={authorizeAccessKey}
      callback={callback}
      chainId={chainId}
      key={`${state}-${pubKey}`}
      keyType={keyType}
      personalSign={personalSign}
      pubKey={pubKey}
      state={state}
    />
  )
}

function MobileAuthFlow(props: {
  authorizeAccessKey: AuthorizeAccessKey
  callback: string
  chainId?: string | undefined
  keyType?: string | undefined
  personalSign?: z.output<typeof Rpc.wallet_connect.personalSign> | undefined
  pubKey: string
  state: string
}) {
  const { authorizeAccessKey, callback, chainId, keyType, personalSign, pubKey, state } = props
  const { capture } = useAnalytics()
  const sessionAddress = Auth.useSessionAddress()
  const connector = useWebAuthnConnector()

  const [draftAuthorizeAccessKey, setDraftAuthorizeAccessKey] = React.useState(authorizeAccessKey)
  const [screen, setScreen] = React.useState<'edit-permissions' | 'review'>('review')

  // Hostname of the requesting app, derived from the redirect callback URL.
  const hostname = (() => {
    try {
      return new URL(callback).host
    } catch {
      return undefined
    }
  })()

  const authorize = useMutation<AuthorizeResult, Error>({
    mutationFn: async () => {
      capture('mobile_auth_authorize_started')

      const targetChainId = chainId ? Number(BigInt(chainId)) : undefined
      if (targetChainId) await switchChain(wagmiConfig, { chainId: targetChainId as never })

      const provider = await connector.getProvider()
      const params = Rpc.wallet_authorizeAccessKey.parameters.parse({
        address: draftAuthorizeAccessKey.address,
        chainId: draftAuthorizeAccessKey.chainId,
        expiry: draftAuthorizeAccessKey.expiry,
        keyType: keyType ?? 'secp256k1',
        limits: draftAuthorizeAccessKey.limits,
        publicKey: pubKey,
      })
      const result = (await provider.request({
        method: 'wallet_authorizeAccessKey',
        params: [z.encode(Rpc.wallet_authorizeAccessKey.parameters as never, params as never)],
      })) as Rpc.wallet_authorizeAccessKey.Encoded['returns']
      const signature = personalSign
        ? ((await provider.request({
            method: 'personal_sign',
            params: [Hex.fromString(personalSign.message), result.rootAddress],
          })) as `0x${string}`)
        : undefined
      if (targetChainId)
        await AuthorizedApps.upsert({
          chainId: targetChainId,
          hostname,
          key: { ...params, address: result.keyAuthorization.address ?? params.address },
        })
      return {
        ...result,
        ...(personalSign ? { personalSign } : {}),
        ...(signature ? { signature } : {}),
      }
    },
    onSuccess: (result) => {
      capture('mobile_auth_authorize_success')
      redirect({
        accountAddress: result.rootAddress,
        callback,
        keyAuthorization: result.keyAuthorization,
        ...(result.personalSign ? { personalSign: result.personalSign } : {}),
        ...(result.signature ? { signature: result.signature } : {}),
        state,
      })
    },
    onError: (error) => {
      capture('mobile_auth_authorize_failed', { error: error.message })
    },
  })

  const connect = useConnect({
    mutation: {
      async onSuccess(data) {
        capture('mobile_auth_authorize_success')
        const account = (data.accounts as any)[0]
        const keyAuthorization = account?.capabilities?.keyAuthorization
        if (!keyAuthorization) return
        const targetChainId = chainId ? Number(BigInt(chainId)) : undefined
        if (targetChainId)
          await AuthorizedApps.upsert({
            chainId: targetChainId,
            hostname,
            key: {
              ...authorizeAccessKey,
              address: keyAuthorization.address ?? authorizeAccessKey.address,
            },
          })
        redirect({
          accountAddress: account.address,
          callback,
          keyAuthorization,
          ...(account.capabilities?.personalSign
            ? { personalSign: account.capabilities.personalSign }
            : {}),
          ...(account.capabilities?.signature ? { signature: account.capabilities.signature } : {}),
          state,
        })
      },
    },
  })

  if (screen === 'edit-permissions')
    return (
      <AccessKeyPermissionsEditor
        authorizeAccessKey={draftAuthorizeAccessKey}
        expiry_original={authorizeAccessKey.expiry}
        onCancel={() => setScreen('review')}
        onSave={(next) => {
          setDraftAuthorizeAccessKey(next)
          setScreen('review')
        }}
      />
    )

  if (sessionAddress)
    return (
      <>
        <AuthorizeSpend
          authorizeAccessKey={draftAuthorizeAccessKey}
          confirming={authorize.isPending}
          error={authorize.error?.message}
          expiry_original={authorizeAccessKey.expiry}
          host={hostname}
          onApprove={() => authorize.mutate()}
          onAuthorizeAccessKeyChange={setDraftAuthorizeAccessKey}
          onReject={() => window.close()}
        />
      </>
    )

  return (
    <Frame
      subtitle="Authorize this app to spend from your account."
      title="Authorize Mobile"
      footerClassName="pt-0"
      footer={
        <>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              const label = new FormData(e.currentTarget).get('label') as string
              connect.mutate({
                connector,
                withCapabilities: true,
                capabilities: {
                  method: 'register',
                  name: label,
                  authorizeAccessKey: draftAuthorizeAccessKey,
                  ...(personalSign ? { personalSign } : {}),
                },
              })
            }}
          >
            <p className="text-copy-15 text-foreground-secondary">
              Create or sign in with your passkey to continue.
            </p>
            <Input name="label" placeholder="Email address or label…" required />
            {(authorize.error || connect.error) && (
              <p className="text-label-13 text-negative">
                {authorize.error?.message ?? connect.error?.message}
              </p>
            )}
            <Button
              loading={connect.isPending && connect.variables?.capabilities?.method === 'register'}
              type="submit"
              variant="primary"
            >
              Create account
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-0 flex-1 border-t border-border" />
              <p className="text-label-12 text-foreground-secondary">or</p>
              <div className="h-0 flex-1 border-t border-border" />
            </div>
            <Button
              loading={connect.isPending && connect.variables?.capabilities?.method === 'login'}
              onClick={() =>
                connect.mutate({
                  connector,
                  withCapabilities: true,
                  capabilities: {
                    method: 'login',
                    authorizeAccessKey: draftAuthorizeAccessKey,
                    ...(personalSign ? { personalSign } : {}),
                    selectAccount: true,
                  },
                })
              }
              icon={<Fingerprint className="size-4" />}
              type="button"
              variant="secondary"
            >
              Sign in with passkey
            </Button>
          </form>
        </>
      }
    >
      <AccessKeyScopes
        authorizeAccessKey={draftAuthorizeAccessKey}
        onEdit={() => setScreen('edit-permissions')}
      />
    </Frame>
  )
}

type AuthorizeResult = Rpc.wallet_authorizeAccessKey.Encoded['returns'] & {
  personalSign?: z.output<typeof Rpc.wallet_connect.personalSign> | undefined
  signature?: `0x${string}` | undefined
}

function buildAuthorizeAccessKey(params: {
  chainId?: string | undefined
  expiry?: string | undefined
  keyType?: string | undefined
  limits?: { limit: string; period?: number | undefined; token: string }[] | undefined
  pubKey: string
}): AuthorizeAccessKey {
  const { chainId, expiry, keyType, limits, pubKey } = params

  const defaultExpiry = Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000)
  const publicKey = pubKey as `0x${string}`

  return {
    address: Address.fromPublicKey(PublicKey.from(publicKey)),
    chainId: chainId ? BigInt(chainId) : undefined,
    expiry: expiry ? Number(expiry) : defaultExpiry,
    keyType: (keyType ?? 'secp256k1') as 'p256' | 'secp256k1' | 'webAuthn',
    limits: limits?.map((l) => ({
      limit: BigInt(l.limit),
      ...(typeof l.period === 'number' ? { period: l.period } : {}),
      token: l.token as `0x${string}`,
    })),
    publicKey,
  }
}

function redirect(params: redirect.Params) {
  const signed = KeyAuthorization.fromRpc(params.keyAuthorization)
  const url = new URL(params.callback)
  url.searchParams.set('accountAddress', params.accountAddress)
  url.searchParams.set('keyAuthorization', KeyAuthorization.serialize(signed))
  if (params.personalSign) url.searchParams.set('personalSignMessage', params.personalSign.message)
  if (params.signature) url.searchParams.set('signature', params.signature)
  url.searchParams.set('state', params.state)
  window.location.href = url.toString()
}

declare namespace redirect {
  type Params = {
    accountAddress: `0x${string}`
    callback: string
    keyAuthorization: z.input<typeof Rpc.keyAuthorization>
    personalSign?: { message: string } | undefined
    signature?: `0x${string}` | undefined
    state: string
  }
}

function parsePersonalSign(
  personalSign: z.output<typeof personalSignSearch>,
): z.output<typeof Rpc.wallet_connect.personalSign> {
  if (typeof personalSign !== 'string') return personalSign
  return Rpc.wallet_connect.personalSign.parse(JSON.parse(personalSign))
}
